import { isLeft } from "fp-ts/lib/Either";
import type { ParsingRule } from "../../../types";
import { veryShort } from "diagnostics-lib/io";
import * as io from "io-ts";
import type { Index } from "diagnostics-lib/iterable";
export const columnsMandatory = [
  ["name"],
  ["quantity"],
  ["minimumValue"],
  ["maximumValue"],
  ["maximumStd"],
];

export const columns = [...columnsMandatory, ["group"], ["default"]];

/**
 * @description Check if file contains all required columns.
 */
export const hasInvalidColumns: ParsingRule = (header: string[]) => {
  const data = columnsMandatory;
  return data.every(columnsM => columnsM.some(col => header?.includes(col))) ? false : true;
};

/**
 * @description Check if file contains all required columns.
 */
export const hasInvalidFileEmpty: ParsingRule = (content: string[]) => content.length === 0;

/**
 * @description Check if the name column, on a given line, contain unexpected value.
 */
export const hasUnexpectedQuantityName: ParsingRule = (
  quantityName: string,
  expectedNameValues: string[],
) => (quantityName && !expectedNameValues.includes(quantityName) ? true : false);

export const hasMinSuperiorToMax = (minimumValue: string, maximumValue: string) =>
  minimumValue &&
  maximumValue &&
  !window.isNaN(parseFloat(minimumValue)) &&
  !window.isNaN(parseFloat(maximumValue)) &&
  parseFloat(minimumValue) >= parseFloat(maximumValue);

export const hasNegativeMaxStd = (maximumStd: string) =>
  !window.isNaN(parseFloat(maximumStd)) && parseFloat(maximumStd) < 0;

export const hasNotNumberValue = (value: string) =>
  value ? window.isNaN(parseFloat(value)) : false;

export const isValuesNotNull = (
  maximumStdString: string,
  maximumValueString: string,
  minimumValueString: string,
) => !maximumStdString && !maximumValueString && !minimumValueString;

export const hasInvalidColumnEmpty = (quantityName: string) => Boolean(quantityName) === false;

export const hasStringVeryShort = (value: string) =>
  isLeft(veryShort(io.string).decode(value.trim()));

export const hasDuplicateValue = (allValues: string[]) => {
  const counts = new Map<string, number>();

  for (const value of allValues) {
    counts.set(value, (counts.get(value) || 0) + 1);
  }

  return [...counts.entries()].filter(([, count]) => count > 1).map(([value]) => value);
};

export const hasQuantityMaxStd = (
  maximumStd: string,
  sourceName: string,
  indexQuantityByName: Index<
    {
      id: string;
      name: string;
      formula: string | null;
    },
    string
  >,
) => {
  const quantity = indexQuantityByName.get(sourceName);
  if (!quantity) return false;
  return maximumStd && quantity.formula !== null;
};
